import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, FileText, Loader2, Send, PauseCircle } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { streamChat, CITATION, IS_CITATION } from "@/lib/chatStream";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/UserAvatar";
import AnswerMeta from "@/components/chat/AnswerMeta";

const bold = (text, key) => text.split(/(\*\*[^*]+\*\*)/g).map((t, j) =>
  /^\*\*[^*]+\*\*$/.test(t) ? <strong key={`${key}-${j}`}>{t.slice(2, -2)}</strong> : <span key={`${key}-${j}`}>{t}</span>);
const render = (content) => content.split(CITATION).map((part, i) =>
  IS_CITATION.test(part)
    ? <span key={i} data-testid="bot-citation" className="font-mono-label text-primary mx-0.5 align-super text-[0.62rem] underline decoration-dotted underline-offset-2">{part}</span>
    : bold(part, i));

export default function BotChat() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const { user } = useAuth();
  const [bot, setBot] = useState(null);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, h] = await Promise.all([api.get(`/b/${token}`), api.get(`/b/${token}/history`)]);
        setBot(b.data);
        setRemaining(b.data.remaining_today);
        setMessages(h.data.flatMap((t) => [
          { id: `${t.id}-q`, role: "user", content: t.question },
          { id: `${t.id}-a`, role: "assistant", content: t.answer, model_label: t.model_label, auto: t.auto, grounded: t.grounded },
        ]));
      } catch (err) {
        setError(errText(err, "This chatbot isn't available."));
      }
    })();
  }, [token]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const patchLast = (patch) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch(copy[copy.length - 1]) };
    return copy;
  });

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: text }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      await streamChat(`/b/${token}/chat`, { message: text }, (j) => {
        if (j.delta) patchLast((last) => ({ content: last.content + j.delta }));
        else if (j.meta) patchLast(() => ({ model_label: j.meta.label, auto: j.meta.auto }));
        else if (j.grounded !== undefined) patchLast(() => ({ grounded: j.grounded }));
        else if (j.error) toast.error(j.error);
      });
      if (remaining != null) setRemaining((r) => Math.max(0, r - 1));
    } catch (e) {
      toast.error(e.message);
      setMessages((m) => m.slice(0, -2));
      setInput(text);
    } finally {
      setStreaming(false);
    }
  };

  const startOver = async () => {
    if (!window.confirm("Start over? Your earlier questions here will be hidden and the chatbot will forget them. Today's question allowance stays the same.")) return;
    try {
      await api.delete(`/b/${token}/history`);
      setMessages([]);
      toast.success("Fresh start");
    } catch (err) {
      toast.error(errText(err, "Couldn't start over"));
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div data-testid="bot-error" className="max-w-sm text-center space-y-3">
          <Bot className="w-10 h-10 mx-auto text-muted-foreground" strokeWidth={1.2} />
          <h1 className="font-serif-display text-3xl">Chatbot unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/app" className="text-sm underline">Go to my library</Link>
        </div>
      </div>
    );
  }
  if (!bot) return <div className="min-h-screen grid place-items-center font-mono-label text-muted-foreground">Loading chatbot…</div>;

  const paused = !bot.enabled && !bot.is_owner;
  const exhausted = remaining === 0;
  const disabled = streaming || paused || exhausted;

  return (
    <div className={`${embed ? "h-screen" : "min-h-screen"} bg-background flex flex-col`}>
      <header className="border-b border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-foreground text-background grid place-items-center shrink-0"><Bot className="w-5 h-5" /></div>
            <div className="min-w-0">
              <h1 data-testid="bot-title" className="font-serif-display text-xl leading-tight truncate">{bot.name}</h1>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                <UserAvatar name={bot.owner?.name} picture={bot.owner?.picture} size={16} testId="bot-owner-avatar" />
                <span data-testid="bot-owner-name" className="truncate">by {bot.owner?.name || "a DocuMind user"}</span>
                <span>·</span><FileText className="w-3 h-3" />
                <span className="truncate">{bot.documents.map((d) => d.filename).join(", ")}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {remaining != null && <span data-testid="bot-remaining" className="font-mono-label text-muted-foreground hidden sm:inline">{remaining} left today</span>}
            {messages.length > 0 && (
              <button data-testid="bot-start-over" disabled={streaming} onClick={startOver} className="text-xs underline disabled:opacity-50">Start over</button>
            )}
            {bot.is_owner && <Link to="/app" data-testid="bot-owner-manage" className="text-xs underline">Manage</Link>}
            {!embed && !bot.is_owner && <Link to="/app" className="text-xs underline hidden sm:inline">My library</Link>}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
          <div data-testid="bot-welcome" className="chat-block">
            <div className="font-mono-label mb-2 text-muted-foreground">{bot.name}</div>
            <p className="text-[15px] leading-relaxed">{bot.welcome || `Ask me anything about ${bot.documents.map((d) => d.filename).join(", ")}.`}</p>
            {messages.length === 0 && bot.suggested?.length > 0 && (
              <div className="grid gap-2 mt-4 sm:grid-cols-1">
                {bot.suggested.map((s) => (
                  <button key={s} data-testid="bot-suggestion" disabled={disabled} onClick={() => send(s)}
                    className="text-left text-sm border border-border px-4 py-3 hover:border-foreground hover:bg-muted disabled:opacity-50">{s}</button>
                ))}
              </div>
            )}
          </div>
          {messages.map((m, i) => (
            <div key={m.id} className={`chat-block ${m.role === "user" ? "user" : ""}`}>
              <div className="font-mono-label mb-2 text-muted-foreground">{m.role === "user" ? (user?.name || "You") : bot.name}</div>
              <div className={`text-[15px] leading-relaxed whitespace-pre-wrap answer-prose ${streaming && i === messages.length - 1 ? "caret" : ""}`}>{render(m.content)}</div>
              {m.role === "assistant" && !(streaming && i === messages.length - 1 && m.grounded === undefined && !m.model_label) && <AnswerMeta message={m} />}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="border-t border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          {paused && (
            <div data-testid="bot-paused-notice" className="mb-3 flex items-center gap-2 border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              <PauseCircle className="w-4 h-4 shrink-0" /> The owner has paused this chatbot for now.
            </div>
          )}
          {exhausted && !paused && (
            <div data-testid="bot-limit-notice" className="mb-3 border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
              You've used today's {bot.visitor_daily_limit} questions here. Come back tomorrow — or <Link to="/app" className="underline">upload your own documents</Link>.
            </div>
          )}
          <div className="flex gap-2 items-end">
            <Textarea data-testid="bot-chat-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={paused || exhausted}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask ${bot.name}…`} rows={2} className="rounded-sm resize-none" />
            <Button data-testid="bot-chat-send" onClick={() => send()} disabled={disabled || !input.trim()} className="h-auto rounded-sm bg-foreground text-background hover:bg-primary btn-invert">
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 flex items-center justify-between">
            <span>Answers come only from the shared documents · {bot.model_label}</span>
            <a href="/" target="_blank" rel="noreferrer" className="hover:underline">Powered by DocuMind</a>
          </p>
        </div>
      </div>
    </div>
  );
}
